/**
 * Utility functions for Google Pay integration
 */

export class GooglePayUtils {
  /**
   * Check if Google Pay is enabled via browser pay parameter
   * @returns {boolean}
   */
  static isBrowserPayEnabled() {
    return js_query_params.with_browser_pay === '1';
  }

  /**
   * Check if Google Pay button should be displayed
   * @param {Object} paymentMethods - available payment methods
   * @returns {boolean}
   */
  static isGooglePayEnabled(paymentMethods) {
    return this.isBrowserPayEnabled() || paymentMethods.google_pay;
  }

  /**
   * Validate Google Pay button prerequisites
   * @param {Object} form - form data
   * @returns {boolean}
   */
  static isButtonValid(form) {
    return !!form.deal && !!form.variant;
  }

  /**
   * Get shipping rates configuration with translation
   * @param {Function} translateFunction - translation function
   * @returns {Array}
   */
  static getShippingRates(translateFunction) {
    return [
      {
        id: 'free',
        displayName: translateFunction('payment.free_shipping'),
        amount: 0
      }
    ];
  }

  /**
   * Build Express Checkout Element options with translations
   * @param {Function} translateFunction - translation function
   * @param {Object} baseConfig - base configuration object
   * @returns {Object}
   */
  static buildExpressCheckoutOptions(translateFunction, baseConfig) {
    return {
      ...baseConfig.EXPRESS_CHECKOUT_OPTIONS,
      shippingRates: this.getShippingRates(translateFunction),
    };
  }

  /**
   * Get currency from product data
   * @returns {string}
   */
  static getCurrency() {
    return js_data.product.prices.currency.toLowerCase();
  }

  /**
   * Check if Stripe library is loaded
   * @returns {boolean}
   */
  static isStripeLoaded() {
    return typeof Stripe !== 'undefined';
  }

  /**
   * Create error message for user display
   * @param {Error|string} error - error object or message
   * @param {Function} translateFunction - translation function
   * @returns {string}
   */
  static createErrorMessage(error, translateFunction) {
    if (typeof error === 'string') {
      return error;
    }
    
    if (error && error.message) {
      return error.message;
    }
    
    return translateFunction('checkout.payment_error');
  }

  /**
   * Log debug information for development
   * @param {string} message - debug message
   * @param {*} data - additional data to log
   */
  static debugLog(message, data = null) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Google Pay Debug] ${message}`, data);
    }
  }

  /**
   * Sanitize and prepare form data for API request
   * @param {Object} formData - raw form data
   * @returns {Object} - sanitized form data
   */
  static sanitizeFormData(formData) {
    return {
      deal: formData.deal,
      variant: formData.variant,
      warranty: Boolean(formData.warranty),
      installments: formData.installments || null
    };
  }

  /**
   * Check if DOM element exists and is visible
   * @param {string} selector - CSS selector for the element
   * @returns {boolean}
   */
  static isElementAvailable(selector) {
    const element = document.querySelector(selector);
    return element !== null && element.offsetParent !== null;
  }

  /**
   * Wait for DOM element to become available
   * @param {string} selector - CSS selector for the element
   * @param {number} timeout - timeout in milliseconds (default: 5000)
   * @param {number} interval - check interval in milliseconds (default: 100)
   * @returns {Promise<boolean>} - resolves to true if element found, false if timeout
   */
  static waitForElement(selector, timeout = 5000, interval = 100) {
    return new Promise((resolve) => {
      const startTime = Date.now();
      
      const checkElement = () => {
        if (this.isElementAvailable(selector)) {
          resolve(true);
          return;
        }
        
        if (Date.now() - startTime >= timeout) {
          resolve(false);
          return;
        }
        
        setTimeout(checkElement, interval);
      };
      
      checkElement();
    });
  }
}

export default GooglePayUtils;